import { Moon, Sun, User, LogOut, CreditCard } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "../ui/dropdown-menu";
import { useToast } from "../ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { redirectToBillingPortal } from "@/integrations/stripe/client";
import { DeleteProfile } from "../DeleteProfile";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../LanguageSwitcher";
import i18next from "i18next";
import { useThemeStore } from "@/stores/use-theme-store";
import { useUser } from "@supabase/auth-helpers-react";
import { useNavigate, useLocation, Link } from "react-router";
import { MixpanelActions } from "@/integrations/mixpanel/mixpanel";
import { SearchBar } from "../community/SearchBar";
import { useSearch } from "@/hooks/useSearch";
import { useTabNavigation } from "@/stores/use-nav-store";
import { useGlobalSearch } from "@/stores/use_search_store";
import { useCategoryStore } from "@/stores/use-tutorial-progress";

export function HomeCommunitySwitcher() {
  const { t } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isCommunity = location.pathname === "/community";

  return (
    <div className="md:flex gap-1 hidden ">
      <Link to="/">
        <Button
          variant={isHome ? "default" : "ghost"}
          className={
            isHome
              ? "font-bold bg-gradient-to-r from-primary to-accent text-white"
              : ""
          }
        >
          {t("menu.home")}
        </Button>
      </Link>
      <Link to="/community">
        <Button
          onClick={() => MixpanelActions.openCommunity()}
          variant={isCommunity ? "default" : "ghost"}
          className={
            isCommunity
              ? "font-bold bg-gradient-to-r from-primary to-accent text-white"
              : ""
          }
        >
          {t("menu.community")}
        </Button>
      </Link>
    </div>
  );
}

export const Header = () => {
  const resetStore = useCategoryStore((state) => state.resetStore);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { search, setSearch } = useGlobalSearch(200);
  const { isLargeScreen } = useTabNavigation(768);
  const user = useUser();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useThemeStore();
  const location = useLocation();
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/login", { replace: true });
      sessionStorage.setItem(
        "tagPreferences",
        JSON.stringify({ included: [], excluded: [] })
      );
      resetStore();
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Error",
        description: "Could not log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      await redirectToBillingPortal(i18next.language);
    } catch (error) {
      console.error("Failed to redirect to billing portal:", error);
      toast({
        title: "Error",
        description: "Could not access billing portal. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center px-4 lg:px-10 sm:max-w-screen-xl mx-auto justify-between">
        <div className="md:flex">
          <a href="/" className="flex items-center">
            <img
              src="/tuties.png"
              alt="Tuties Logo"
              className="h-14 w-auto hidden lg:block"
            />
            <img
              src="/mini-logo.png"
              alt="Tuties Logo"
              className="h-8 w-auto block lg:hidden"
            />
          </a>
        </div>
        {!isLargeScreen && location.pathname === "/community" && (
          <SearchBar
            isUpside={true}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
        <HomeCommunitySwitcher />
        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={toggleTheme}
          >
            {theme === "light" ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 hidden md:flex"
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-background border shadow-lg"
            >
              <DropdownMenuLabel className="bg-background">
                <div className="flex flex-col space-y-1">
                  <Link to={`/user/${user.id}`}>
                    <p className="text-sm font-medium leading-none text-foreground">
                      {user?.email}
                    </p>
                  </Link>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <LanguageSwitcher />
                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={handleManageSubscription}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>{t("menu.billing")}</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DeleteProfile user={user} />
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t("menu.logout")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
